import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import { useMemo } from 'react';

import modalStyles, { DIALOG_PAPER_SLOT } from './styles';

interface EvolutionForm {
  created_at: string;
  text: string;
  id: number;
  pictures: string[];
}

interface EvolutionModalProps {
  data?: EvolutionForm;
  isOpen: boolean;
  handleClose: () => void;
}

const EvolutionModal = ({
  isOpen = false,
  handleClose,
  data,
}: EvolutionModalProps): JSX.Element => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const imageCols = isXs ? 1 : 2;

  const pictureItems = useMemo(() => {
    if (!data?.pictures?.length) {
      return null;
    }
    return (
      <ImageList cols={imageCols} gap={12} sx={modalStyles.imageList}>
        {data.pictures.map((picture, index) => (
          <ImageListItem
            // eslint-disable-next-line react/no-array-index-key
            key={`ev-pic-${index}`}
            sx={modalStyles.imageListItem}
          >
            <img
              src={picture}
              alt={`Evolução — imagem ${index + 1}`}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: 280,
                objectFit: 'cover',
                display: 'block',
                borderRadius: 8,
              }}
            />
          </ImageListItem>
        ))}
      </ImageList>
    );
  }, [data?.pictures, imageCols]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="evolution-view-title"
      slotProps={{ paper: DIALOG_PAPER_SLOT }}
    >
      <DialogTitle id="evolution-view-title" sx={modalStyles.dialogTitle}>
        <Box sx={modalStyles.titleRow}>
          <Typography variant="h6" sx={modalStyles.titleTypography}>
            Detalhes da evolução
          </Typography>
          <IconButton edge="end" onClick={handleClose} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={modalStyles.dialogContent}>
        {data ? (
          <Stack spacing={3}>
            <Stack spacing={0.5}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={modalStyles.overlineBlock}
              >
                Data
              </Typography>
              <Typography variant="body1" sx={modalStyles.dateValue}>
                {new Date(data.created_at).toLocaleDateString()}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={modalStyles.overlineBlock}
              >
                Relatório
              </Typography>
              <Typography variant="body1" sx={modalStyles.reportBody}>
                {data.text}
              </Typography>
            </Stack>
            {pictureItems && (
              <Stack spacing={1}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={modalStyles.overlineBlock}
                >
                  Fotos
                </Typography>
                {pictureItems}
              </Stack>
            )}
          </Stack>
        ) : (
          <Typography color="text.secondary">
            Nenhum dado encontrado para esse relatório.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EvolutionModal;
